/**
 * `Sidebar` - the fixed left rail: brand lockup, grouped module navigation,
 * and the build / environment / tenant footer.
 *
 * 232px wide, full height, `--side` background, nav grouped under OPERATE /
 * MONEY / OVERSIGHT / CONFIGURE / PLATFORM. The nav model comes from
 * `module-registry.ts` - this component decides only how it looks, never
 * what is in it.
 *
 * This replaces the top-nav-only chrome that the original UX pack specified
 * for M1. That decision was taken when the app had one feature and a rail
 * with one item would have been noise; the platform-module IA now has 25
 * entries, so the rail carries its weight. See the MUR "Information
 * Architecture and Navigation" page.
 *
 * ## The rail is an accordion
 *
 * Twenty-five modules will not fit a fixed rail: fully expanded it is about
 * 1,110px against roughly 680px of available height at 1280x800, the
 * smallest supported viewport. One group is open at a time, opened by the
 * current route, so the rail never scrolls and the current module is always
 * in view. `useNavAccordion` owns the state and the rule that the current
 * group cannot be collapsed; the measured heights behind the decision are
 * on the MUR IA page.
 *
 * A collapsed group shows a count of the modules inside it, so the rail
 * still communicates the size of the platform when everything is shut.
 *
 * ## The Platform group
 *
 * `Platform` is Murmuration's own control plane and renders only for a
 * Murmuration role. **Hiding the nav entry is presentation, not the
 * control** - the routes are rejected server-side, and a credit union user
 * who types the URL must be stopped there, not here.
 *
 * Accessibility:
 *   - `<nav aria-label="Platform modules">` so it is distinguishable from
 *     the breadcrumb nav in the header bar.
 *   - Each group is a `<ul aria-label>`, so a screen reader announces
 *     "Operate, list, 6 items" rather than a flat run of 25 links.
 *   - Group toggles are `<button aria-expanded>` and deliberately NOT
 *     headings. They sit before the page `<h1>` in DOM order, so making
 *     them `<h2>` gives every screen an outline of h2 before h1 - a
 *     heading-order violation axe rates "moderate", which a
 *     serious/critical gate would miss. The list's `aria-label` carries the
 *     same information without entering the outline.
 *   - Group labels are stored sentence-case and uppercased with CSS, so
 *     assistive tech reads "Operate", not "O-P-E-R-A-T-E".
 *   - The count is spoken as "Money, 7 modules" rather than as a bare digit
 *     with no referent, and the accessible name does not change when the
 *     group opens.
 *   - Keyboard: Tab reaches each group button in order; Enter and Space
 *     toggle; Arrow keys move between links within an open group; Home and
 *     End jump to its first and last.
 */
import { useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { isMurmurationStaff } from '../auth/claims'
import { useAuth } from '../auth/use-auth'
import {
  NAV_GROUPS,
  navEntriesForGroup,
  type NavGroupKey,
} from '../features/modules/module-registry'
import { Brand } from './brand'
import { FOCUS_RING_ON_DARK_CLASS } from './focus-ring'
import { NavItem } from './nav-item'
import { TenantChip } from './tenant-chip'
import { useNavAccordion } from './use-nav-accordion'

/**
 * The group owning a pathname, or null when no module matches (sign-in and
 * the terminal error screens). Longest route wins, so `/platform/tenants`
 * is not shadowed by a shorter prefix.
 */
function groupForPath(pathname: string): NavGroupKey | null {
  const match = NAV_GROUPS.flatMap((group) =>
    navEntriesForGroup(group.key).map((entry) => ({ group: group.key, route: entry.route })),
  )
    .filter(({ route }) => pathname === route || pathname.startsWith(`${route}/`))
    .sort((a, b) => b.route.length - a.route.length)[0]
  return match?.group ?? null
}

export function Sidebar(): JSX.Element {
  const env = import.meta.env.VITE_ENV ?? 'dev'
  const { pathname } = useLocation()
  const { claims } = useAuth()

  const murmurationStaff = isMurmurationStaff(claims)
  const visibleGroups = NAV_GROUPS.filter((group) => !group.murmurationOnly || murmurationStaff)

  const accordion = useNavAccordion({
    groups: visibleGroups.map((group) => group.key),
    currentGroup: groupForPath(pathname),
  })

  const listRefs = useRef(new Map<NavGroupKey, HTMLUListElement | null>())

  // Roving movement inside an open group. Tab still steps through the links
  // one at a time - this is movement on top of it, not a replacement for
  // it, so no link is given a negative tabindex.
  const handleListKeyDown = useCallback(
    (group: NavGroupKey) => (event: React.KeyboardEvent<HTMLUListElement>) => {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
      const list = listRefs.current.get(group)
      if (!list) return
      const links = [...list.querySelectorAll<HTMLAnchorElement>('a')]
      if (links.length === 0) return
      const index = links.indexOf(document.activeElement as HTMLAnchorElement)
      if (index === -1) return

      event.preventDefault()
      const next =
        event.key === 'ArrowDown'
          ? (index + 1) % links.length
          : event.key === 'ArrowUp'
            ? (index - 1 + links.length) % links.length
            : event.key === 'Home'
              ? 0
              : links.length - 1
      links[next]?.focus()
    },
    [],
  )

  return (
    <nav
      aria-label="Platform modules"
      className="flex h-full w-sidebar flex-none flex-col overflow-hidden bg-side text-side-tx"
    >
      <div className="px-murmuration-5 pb-murmuration-3 pt-murmuration-5">
        <Brand to="/members" variant="sidebar" />
      </div>

      <div className="flex flex-1 flex-col gap-[2px] overflow-y-auto px-murmuration-3 pb-murmuration-4">
        {visibleGroups.map((group) => {
          const entries = navEntriesForGroup(group.key)
          if (entries.length === 0) return null
          const open = accordion.isOpen(group.key)
          return (
            <div key={group.key} className="flex flex-col">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => accordion.toggle(group.key)}
                className={[
                  'flex w-full items-center gap-murmuration-2 rounded-md bg-transparent px-[8px] pb-[7px] pt-[9px]',
                  'text-left text-murmuration-group uppercase text-side-label',
                  'transition-colors duration-murmuration-fast hover:text-side-tx',
                  FOCUS_RING_ON_DARK_CLASS,
                ].join(' ')}
              >
                <Chevron open={open} />
                <span>{group.label}</span>
                {/* The count is visible only when the group is closed - open,
                    the items speak for themselves. It stays in the
                    accessible tree either way, so the button reads "Money, 7
                    modules" in both states and does not rename itself
                    mid-interaction. */}
                <span
                  className={open ? 'sr-only' : 'ml-auto text-[10px] font-semibold text-side-dot'}
                >
                  {entries.length}
                </span>
                <span className="sr-only">modules</span>
              </button>
              <ul
                aria-label={group.label}
                hidden={!open}
                ref={(node) => {
                  listRefs.current.set(group.key, node)
                }}
                onKeyDown={handleListKeyDown(group.key)}
                // `hidden` alone is not enough: it sets `display: none` from
                // the UA stylesheet, which any `display` utility class beats
                // on specificity. A `flex` here left every group painted in
                // the browser while jsdom - which has no Tailwind - happily
                // reported them hidden, so the unit tests passed and the
                // real rail showed 25 modules at once. The class carries the
                // display; the attribute carries the semantics.
                className={open ? 'mb-murmuration-1 flex flex-col gap-[2px]' : 'hidden'}
              >
                {entries.map((entry) => (
                  <li key={entry.key}>
                    <NavItem to={entry.route} label={entry.label} implemented={entry.implemented} />
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>

      <div className="mt-auto flex flex-none flex-col gap-murmuration-1 border-t border-white/10 px-murmuration-5 py-murmuration-3">
        <span className="text-murmuration-micro text-side-label">
          murmuration v0.1.0 &middot; env:{env}
        </span>
        <TenantChip tenant={claims?.tnt} canSwitch={murmurationStaff} />
      </div>
    </nav>
  )
}

/** Points right when the group is closed, down when open. Decorative. */
function Chevron({ open }: { readonly open: boolean }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 8 8"
      className={[
        'h-2 w-2 flex-none transition-transform duration-murmuration-fast',
        open ? '' : '-rotate-90',
      ].join(' ')}
    >
      <path
        d="M1 2.5 4 5.5 7 2.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}
